import React from "react";
import {
  FieldProps,
  InputFieldsControllerProps,
} from "./InputFieldsController.utils";

// Field components
import TextInput from "../TextInput/TextInput";
import ContactNumberInput from "../ContactNumberInput/ContactNumberInput";
import PasswordInput from "../PasswordInput/PasswordInput";
import TextareaInput from "../TextareaInput/TextareaInput";
import SelectInput from "../SelectInput/SelectInput";
import DateInput from "../DateInput/DateInput";
import CheckboxInput from "../CheckboxInput/CheckboxInput";
import RadioInput from "../RadioInput/RadioInput";
import FileUploadInput from "../FileUploadInput/FileUploadInput";
import Header from "../Header/Header";
import SSNInput from "../SSNInput/SSNInput";
import EINInput from "../EINInput/EINInput";
import CurrencyInput from "../CurrencyInput/CurrentcyInput";
import SliderInput from "../SliderInput/SliderInput";
import NumberInput from "../NumberInput/NumberInput";

/**
 * Dynamically renders form field components based on their type.
 * Supports flexible layout, validation props, and runtime styling.
 */
const InputFieldsController: React.FC<InputFieldsControllerProps> = ({
  field,
  handleChange,
  handleBlur,
  handleSelect,
  onInputChange
}) => {
  if (!field?.type || !field?.id) return null;

  const {
    margins = {
      top: 5,
      right: 5,
      bottom: 5,
      left: 5,
    },
    textStyle = {
      ...field.textStyle,
      margins: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
  } = field;

  const fieldWithDefaults: FieldProps = {
    ...field,
    margins,
    textStyle,
  };

  switch (field.type) {
    case "text":
      return (
        <TextInput
          field={fieldWithDefaults}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "textarea":
      return (
        <TextareaInput
          field={field}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "password":
      return (
        <PasswordInput
          field={field}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "contact":
      return (
        <ContactNumberInput
          field={fieldWithDefaults}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "numberinput":
      return (
        <NumberInput
          field={fieldWithDefaults}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "ssn":
      return (
        <SSNInput
          field={fieldWithDefaults}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "ein":
      return (
        <EINInput
          field={fieldWithDefaults}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "currency":
      return (
        <CurrencyInput
          field={fieldWithDefaults}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "select":
      return (
        <SelectInput
          field={field}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
          {...(onInputChange ? { onInputChange } : {})}
        />
      );
    case "date":
      return (
        <DateInput
          field={field}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "checkbox":
      return (
        <CheckboxInput
          field={field}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "slider":
      return (
        <SliderInput
          field={field}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "radio":
      return (
        <RadioInput
          field={field}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "file":
      return (
        <FileUploadInput
          field={field}
          handleChange={handleChange}
          handleBlur={handleBlur}
          handleSelect={handleSelect}
        />
      );
    case "header":
      return <Header field={field} handleSelect={handleSelect} />;

    default:
      return null;
  }
};

export default InputFieldsController;
